/**
 * useItemCategories Hook
 * Manages item categories with full CRUD operations
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { 
  ItemCategory, 
  ItemCategoryFormData,
  UseItemCategoriesReturn 
} from '@/types/itemMaster';

export function useItemCategories(): UseItemCategoriesReturn {
  const { toast } = useToast();
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [flatCategories, setFlatCategories] = useState<ItemCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Build hierarchical tree from flat categories
   */
  const buildCategoryTree = useCallback((flatList: ItemCategory[]): ItemCategory[] => {
    const categoryMap = new Map<string, ItemCategory>();
    const rootCategories: ItemCategory[] = [];

    // First pass: create map of all categories
    flatList.forEach(cat => {
      categoryMap.set(cat.id, { ...cat, children: [] });
    });

    // Second pass: build tree structure
    flatList.forEach(cat => {
      const category = categoryMap.get(cat.id)!;
      if (cat.parent_category_id) {
        const parent = categoryMap.get(cat.parent_category_id);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(category);
        } else {
          rootCategories.push(category);
        }
      } else {
        rootCategories.push(category);
      }
    });

    // Sort by sort_order
    const sortCategories = (cats: ItemCategory[]): ItemCategory[] => {
      return cats
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(cat => ({
          ...cat,
          children: cat.children ? sortCategories(cat.children) : []
        }));
    };

    return sortCategories(rootCategories);
  }, []);

  /**
   * Fetch all categories
   */
  const fetchCategories = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('item_categories')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;

      const flatList = data || [];
      setFlatCategories(flatList);
      setCategories(buildCategoryTree(flatList));

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch categories';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [buildCategoryTree, toast]);

  /**
   * Get a single category by ID
   */
  const getCategory = useCallback(async (id: string): Promise<ItemCategory | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('item_categories')
        .select(`
          *,
          parent_category:parent_category_id(id, code, name)
        `)
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      return data;

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch category';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  /**
   * Create a new category
   */
  const createCategory = useCallback(async (formData: ItemCategoryFormData): Promise<ItemCategory | null> => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      const { data, error: insertError } = await supabase
        .from('item_categories')
        .insert({
          code: formData.code,
          name: formData.name,
          description: formData.description || null,
          parent_category_id: formData.parent_category_id || null,
          is_active: formData.is_active ?? true,
          sort_order: formData.sort_order ?? 0,
          created_by: user?.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      toast({
        title: 'Success',
        description: `Category "${formData.name}" created successfully`,
      });

      // Refresh categories
      await fetchCategories();
      return data;

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create category';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchCategories, toast]);

  /**
   * Update an existing category
   */
  const updateCategory = useCallback(async (
    id: string, 
    formData: Partial<ItemCategoryFormData>
  ): Promise<ItemCategory | null> => {
    try {
      setLoading(true);
      setError(null);

      // Prevent circular reference
      if (formData.parent_category_id === id) {
        throw new Error('A category cannot be its own parent');
      }

      const updateData: Record<string, unknown> = {};
      if (formData.code !== undefined) updateData.code = formData.code;
      if (formData.name !== undefined) updateData.name = formData.name;
      if (formData.description !== undefined) updateData.description = formData.description;
      if (formData.parent_category_id !== undefined) updateData.parent_category_id = formData.parent_category_id;
      if (formData.is_active !== undefined) updateData.is_active = formData.is_active;
      if (formData.sort_order !== undefined) updateData.sort_order = formData.sort_order;

      const { data, error: updateError } = await supabase
        .from('item_categories')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Category updated successfully',
      });

      // Refresh categories
      await fetchCategories();
      return data;

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update category';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchCategories, toast]);

  /**
   * Delete a category
   */
  const deleteCategory = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Check if category has children
      const { data: children } = await supabase
        .from('item_categories')
        .select('id')
        .eq('parent_category_id', id)
        .limit(1);

      if (children && children.length > 0) {
        throw new Error('Cannot delete category with sub-categories. Delete or reassign sub-categories first.');
      }

      // Check if category is used by items
      const { data: items } = await supabase
        .from('inventory_items')
        .select('id')
        .eq('category_id', id)
        .limit(1);

      if (items && items.length > 0) {
        throw new Error('Cannot delete category that is assigned to items. Reassign items first.');
      }

      const { error: deleteError } = await supabase
        .from('item_categories')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      toast({
        title: 'Success',
        description: 'Category deleted successfully',
      });

      // Refresh categories
      await fetchCategories();
      return true;

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete category';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchCategories, toast]);

  /**
   * Get category tree (hierarchical structure)
   */
  const getCategoryTree = useCallback((): ItemCategory[] => {
    return categories;
  }, [categories]);

  return {
    categories,
    flatCategories,
    loading,
    error,
    fetchCategories,
    getCategory,
    createCategory,
    updateCategory,
    deleteCategory,
    getCategoryTree,
  };
}

export default useItemCategories;
